"use client"

import { useTheme } from "@emotion/react"
import styled from "@emotion/styled"
import { useTranslations } from "next-intl"
import {
  BalconyIcon,
  BuildingIcon,
  CheckNewIcon,
  Floor2Icon,
  NewText,
  Rooms2Icon,
  SquareMetersIcon,
} from "ui"
import { Apartment } from "utils"

const SpecsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const SpecsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }
`

const CheckmarksGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 24px;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: contents;
  }
`

const IconSpecsColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 24px;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: flex;
    flex-direction: column;
    width: 158px;
    flex-shrink: 0;
  }
`

const Char = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
`

const CheckChar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
`

type Props = {
  apartment: Apartment
}
function ApartmentFeatures({ apartment }: Props) {
  const { colors } = useTheme()
  const t = useTranslations("AccordionDetails")

  // Collect all checkmark items
  const checkmarkItems: string[] = []
  if (apartment.orientation) {
    checkmarkItems.push(`${t("direction")} ${apartment.orientation}`)
  }
  if (typeof apartment?.houseCommitee === "number" || apartment?.houseCommitee) {
    checkmarkItems.push(`${t("boardfee")} ${apartment.houseCommitee} ₪`)
  }
  checkmarkItems.push(...apartment.apartmentCharacteristics)

  // Split checkmarks into 2 columns
  const midPoint = Math.ceil(checkmarkItems.length / 2)
  const rightCheckmarks = checkmarkItems.slice(0, midPoint)
  const leftCheckmarks = checkmarkItems.slice(midPoint)

  return (
    <SpecsContainer>
      {apartment.marketingText && (
        <NewText
          component={"p"}
          desktopFontSize={16}
          color={colors.newBlack}
          desktopLineHeight={"normal"}
          mobileFontSize={16}
          mobileFontWeight={400}
          desktopFontWeight={400}
          mobileLineHeight={"normal"}
        >
          {apartment.marketingText}
        </NewText>
      )}
      <SpecsGrid>
        {/* Icon specs column (right in RTL = first child) */}
        <IconSpecsColumn>
          <Char>
            <Rooms2Icon style={{ flexShrink: 0 }} />
            <NewText
              component={"span"}
              desktopFontSize={16}
              color={colors.newBlack}
              desktopLineHeight={"normal"}
              mobileFontSize={16}
              mobileFontWeight={400}
              desktopFontWeight={400}
              mobileLineHeight={"normal"}
              sx={{ whiteSpace: "nowrap" }}
            >
              {apartment.numberOfRooms} {t("numberOfRooms")}
            </NewText>
          </Char>
          <Char>
            <BuildingIcon style={{ flexShrink: 0 }} />
            <NewText
              component={"span"}
              desktopFontSize={16}
              color={colors.newBlack}
              desktopLineHeight={"normal"}
              mobileFontSize={16}
              mobileFontWeight={400}
              desktopFontWeight={400}
              mobileLineHeight={"normal"}
              sx={{ whiteSpace: "nowrap" }}
            >
              {t("building")} {apartment.buildingTitle}
            </NewText>
          </Char>
          <Char>
            <SquareMetersIcon style={{ flexShrink: 0 }} />
            <NewText
              component={"span"}
              desktopFontSize={16}
              color={colors.newBlack}
              desktopLineHeight={"normal"}
              mobileFontSize={16}
              mobileFontWeight={400}
              desktopFontWeight={400}
              mobileLineHeight={"normal"}
              sx={{ whiteSpace: "nowrap" }}
            >
              {apartment.areaInSquareMeters} {t("sqm")}
            </NewText>
          </Char>
          <Char>
            <BalconyIcon style={{ flexShrink: 0 }} />
            <NewText
              component={"span"}
              desktopFontSize={16}
              color={colors.newBlack}
              desktopLineHeight={"normal"}
              mobileFontSize={16}
              mobileFontWeight={400}
              desktopFontWeight={400}
              mobileLineHeight={"normal"}
              sx={{ whiteSpace: "nowrap" }}
            >
              {apartment.balconySquareMeter} {t("balconySqm")}
            </NewText>
          </Char>
          {apartment.floor && (
            <Char>
              <Floor2Icon style={{ flexShrink: 0 }} />
              <NewText
                component={"span"}
                desktopFontSize={16}
                color={colors.newBlack}
                desktopLineHeight={"normal"}
                mobileFontSize={16}
                mobileFontWeight={400}
                desktopFontWeight={400}
                mobileLineHeight={"normal"}
                sx={{ whiteSpace: "nowrap" }}
              >
                {t("floor")} {apartment.floor}
              </NewText>
            </Char>
          )}
        </IconSpecsColumn>
        <CheckmarksGrid>
          {/* Middle column: checkmarks (first half) */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            {rightCheckmarks.map((el, i) => (
              <CheckChar key={`right-${i}`}>
                <CheckNewIcon style={{ flexShrink: 0 }} />
                <NewText
                  component={"span"}
                  desktopFontSize={16}
                  color={colors.newBlack}
                  desktopLineHeight={"normal"}
                  mobileFontSize={16}
                  mobileFontWeight={400}
                  desktopFontWeight={400}
                  mobileLineHeight={"normal"}
                >
                  {el}
                </NewText>
              </CheckChar>
            ))}
          </div>
          {/* Left column: checkmarks (second half) */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            {leftCheckmarks.map((el, i) => (
              <CheckChar key={`left-${i}`}>
                <CheckNewIcon style={{ flexShrink: 0 }} />
                <NewText
                  component={"span"}
                  desktopFontSize={16}
                  color={colors.newBlack}
                  desktopLineHeight={"normal"}
                  mobileFontSize={16}
                  mobileFontWeight={400}
                  desktopFontWeight={400}
                  mobileLineHeight={"normal"}
                >
                  {el}
                </NewText>
              </CheckChar>
            ))}
          </div>
        </CheckmarksGrid>
      </SpecsGrid>
    </SpecsContainer>
  )
}
export default ApartmentFeatures
